import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FieldsetAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NutritionMilkAndDerivativesViewFragmentAliases } from '../../../../../codegen';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { AdministrationRouteType, MilkAndDerivativesTypes } from './milk-and-derivatives-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';
import { AdministrationType, DurationType } from '../../cpoe-view-types';

@Controller({ fragment: FragmentNames.NUTRITION_MILK_AND_DERIVATIVES_VIEW_FRAGMENT })
class MilkAndDerivativesViewController extends BaseController {

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FORM_ADMINISTRATION_INFORMATION)
  formAdministrationInformationAPI!: FormAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_JUSTIFICATION)
  fieldJustificationAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDtAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_PRESCRIBER_NAME)
  fieldPrescriberNameAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_INFUSION_RATE)
  fieldInfusionRateAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_INFUSION_RATE_UNIT_OF_MEASUREMENT)
  fieldInfusionRateUnitOfMeasurementAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_DURATION_H_MIN)
  fieldDurationHMinAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_SUCTION_DEVICE)
  fieldSuctionDeviceAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELD_TEMPERATURE)
  fieldTemperatureAPI!: FieldAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELDSET_ITEM_1)
  fieldsetItem1API!: FieldsetAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELDSET_ITEM_2)
  fieldsetItem2API!: FieldsetAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELDSET_ITEM_3)
  fieldsetItem3API!: FieldsetAPI;

  @Inject(NutritionMilkAndDerivativesViewFragmentAliases.FIELDSET_ITEM_4)
  fieldsetItem4API!: FieldsetAPI;

  @OnMount(NutritionMilkAndDerivativesViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nutritionId, patientId } = this.historyService.getCurrentRouteParams();
    this.fetchMilkAndDerivativesDetails(String(nutritionId), String(patientId));
  }

  @OnMount(NutritionMilkAndDerivativesViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const milkAndDerivativesView = this.context.get('milkAndDerivativesView') as MilkAndDerivativesTypes;
    if (milkAndDerivativesView) {
      const {
        item,
        administrationRoute1,
        dose1,
        measurementUnitDescription,
        additionalItem1,
        additionalItem1Dose,
        additionalItem1Id,
        additionalItem1MeasurementUnit,
        additionalItem2,
        additionalItem2Dose,
        additionalItem2Id,
        additionalItem2MeasurementUnit,
        additionalItem3,
        additionalItem3Dose,
        additionalItem3Id,
        additionalItem3MeasurementUnit,
        additionalItem4,
        additionalItem4Dose,
        additionalItem4Id,
        additionalItem4MeasurementUnit,
        additionalPercentageItem1,
        additionalPercentageItem2,
        additionalPercentageItem3,
        additionalPercentageItem4
      } = milkAndDerivativesView;

      this.formMainInformationsAPI.setValue({
        additionalItem1,
        additionalItem1Dose,
        additionalItem1MeasurementUnit,
        additionalItem2,
        additionalItem2Dose,
        additionalItem2MeasurementUnit,
        additionalItem3,
        additionalItem3Dose,
        additionalItem3MeasurementUnit,
        additionalItem4,
        additionalItem4Dose,
        additionalItem4MeasurementUnit,
        additionalPercentageItem1,
        additionalPercentageItem2,
        additionalPercentageItem3,
        additionalPercentageItem4,
        administrationRoute1,
        dose1,
        item,
        measurementUnitDescription
      });

      this.fieldsetItem1API.setVisible(!!additionalItem1Id)
      this.fieldsetItem2API.setVisible(!!additionalItem2Id)
      this.fieldsetItem3API.setVisible(!!additionalItem3Id)
      this.fieldsetItem4API.setVisible(!!additionalItem4Id)
    }
  }

  @OnMount(NutritionMilkAndDerivativesViewFragmentAliases.FORM_ADMINISTRATION_INFORMATION)
  handleMountFormAdministrationInformation(): void {
    const milkAndDerivativesView = this.context.get('milkAndDerivativesView') as MilkAndDerivativesTypes
    if (milkAndDerivativesView) {
      const { interval, suctionDevice, applicationTime, dosageType, infusionRate, temperature, totalVolume } = milkAndDerivativesView;
      this.formAdministrationInformationAPI.setValue({
        applicationTime,
        dosageType,
        infusionRate,
        interval,
        suctionDevice,
        temperature,
        totalVolume
      });
      this.fieldSuctionDeviceAPI.setVisible(!!suctionDevice);
      this.fieldInfusionRateAPI.setVisible(!!infusionRate);
      this.fieldDurationHMinAPI.setVisible(!!applicationTime)
      this.showFieldsByAdministrationRouteType(milkAndDerivativesView);
    }
  }

  @OnMount(NutritionMilkAndDerivativesViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const milkAndDerivativesView = this.context.get('milkAndDerivativesView') as MilkAndDerivativesTypes;

    if (milkAndDerivativesView) {
      const { duration, hours, administration, administrationType, durationType } = milkAndDerivativesView;
      const startDate = moment(String(milkAndDerivativesView.startDate)).utc().format(ISO_DATE_INPUT);
      const endDate = moment(String(milkAndDerivativesView.endDate)).utc().format(ISO_DATE_INPUT);

      this.formPeriodValidityAPI.setValue({
        administration,
        duration,
        endDate,
        hours,
        startDate
      });

      this.inputStartDateAPI.setValue(startDate);
      this.inputEndDateAPI.setValue(endDate);
      this.showFieldsByDurationType(durationType);
      this.showFieldsByAdministrationType(administrationType);
    }
  }

  @OnMount(NutritionMilkAndDerivativesViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const milkAndDerivativesView = this.context.get('milkAndDerivativesView') as MilkAndDerivativesTypes;
    if (milkAndDerivativesView) {
      const { prescriberDt, prescriberName, observation, justification } = milkAndDerivativesView;

      this.formAddicionalInformationsAPI.setValue({
        justification,
        observation,
        prescriberDt,
        prescriberName
      });

      this.fieldObservationAPI.setVisible(!!observation);
      this.fieldJustificationAPI.setVisible(!!justification);
      this.fieldPrescriberNameAPI.setVisible(!!prescriberName);
      this.fieldPrescriberDtAPI.setVisible(!!prescriberDt);
    }
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED);
  }

  private showFieldsByAdministrationType(administration: string): void {
    this.fieldHoursAPI.setVisible(administration === AdministrationType.AS_START_TIME);
  }

  private showFieldsByAdministrationRouteType({ administrationRouteType, temperature }: MilkAndDerivativesTypes): void {
    if (administrationRouteType === AdministrationRouteType.ORAL) {
      this.fieldDurationHMinAPI.setVisible(false);
      this.fieldInfusionRateAPI.setVisible(false);
      this.fieldInfusionRateUnitOfMeasurementAPI.setVisible(false);
      this.fieldTemperatureAPI.setVisible(!!temperature)
    } else {
      this.fieldTemperatureAPI.setVisible(false)
    }

    const typesNotEnabledForSuctionDevices = [
      AdministrationRouteType.IM_INJECTION.valueOf(),
      AdministrationRouteType.INTRAVENOUS.valueOf(),
      AdministrationRouteType.INJECTION.valueOf(),
      AdministrationRouteType.INJ1.valueOf(),
      AdministrationRouteType.EPIDURAL.valueOf(),
      AdministrationRouteType.INFUSIONS.valueOf()
    ]

    if (typesNotEnabledForSuctionDevices.includes(administrationRouteType)) {
      this.fieldSuctionDeviceAPI.setVisible(false);
    }
  }

  private fetchMilkAndDerivativesDetails(nutritionId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true);
    this.panelContentAPI.setVisible(false);
    HttpService.get(Endpoint.GET_CPOE_NUTRITION_MILK_AND_DERIVATIVES_DETAILS, { nutritionId, patientId })
      .then((response: HttpResponse) => {
        const milkAndDerivativesView = response.body as MilkAndDerivativesTypes;
        this.context.set({ milkAndDerivativesView });
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false);
        this.panelContentAPI.setVisible(true);
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nutrition-milk-and-derivatives',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }
}

export default MilkAndDerivativesViewController;